import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Spinner, Tabs, Typography } from '@tempmee/react';
import { Center, ClickAwayListener, Column, Pressable } from '~/commonComponents';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { ProfessionTypeEnum } from '~/models';
import { firePopUpViewed, fireInviteSent, type TInvitePopupSource } from '~/posthogs/events';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { ProfessionalHubRedesign } from '~/constants/ff.posthog';

import {
  Container,
  FavoriteTabPanel,
  Overlay,
  RecentProfessionalTabPanel,
  RecommendedProsTabPanel,
} from './components';
import { ViewSkillsScreen } from './components/ViewSkillsScreen';
import { useInviteProfessional } from './hooks';
import { OutlineXIcon } from '@tempmee/react-icons';

interface IProps {
  isOpen: boolean;
  isRefetchJob: boolean;
  confirmButtonText?: string;
  jobId?: string;
  jobIds?: string[];
  defaultProfessionals?: string[];
  profession?: string;
  officeId?: string;
  onClose: () => void;
  onConfirm?: (ids: string[]) => void;
  onTrackEvent?: () => void;
  trackingSource?: TInvitePopupSource;
}

export const InviteProfessionalPopup = ({
  isOpen,
  isRefetchJob,
  confirmButtonText,
  jobId,
  jobIds,
  defaultProfessionals,
  profession,
  officeId,
  onClose,
  onConfirm,
  onTrackEvent,
  trackingSource,
}: IProps) => {
  const isNewDesign = useFeatureFlag(ProfessionalHubRedesign);
  const buttonText = confirmButtonText ?? (isNewDesign ? 'Confirm' : 'Send Invites');
  const [viewSkillsProfessional, setViewSkillsProfessional] = useState<any>(null);

  const {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange,
    favoriteProfessionals,
    recentProfessionals,
    recommendedProfessionals,
    sentProfessionalIds,
    jobProfession,
  } = useInviteProfessional({
    isRefetchJob,
    jobId,
    jobIds,
    defaultProfessionals,
    officeId,
    isOpen,
    onClosePopup: onClose,
    filterProfession: profession as ProfessionTypeEnum,
  });

  const allSelectableIds = useMemo(() => {
    if (!isNewDesign) return undefined;
    const ids = new Set<string>();

    favoriteProfessionals?.forEach((p: any) => {
      if (p.id && !sentProfessionalIds.has(p.id)) ids.add(p.id);
    });
    recentProfessionals?.forEach((p: any) => {
      const id = p.professional?.id ?? p.id;
      if (id && !sentProfessionalIds.has(id)) ids.add(id);
    });
    recommendedProfessionals?.forEach((p: any) => {
      if (p.id && !sentProfessionalIds.has(p.id)) ids.add(p.id);
    });

    return [...ids];
  }, [
    isNewDesign,
    favoriteProfessionals,
    recentProfessionals,
    recommendedProfessionals,
    sentProfessionalIds,
  ]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && trackingSource) {
      firePopUpViewed({ source: trackingSource });
    }
  }, [isOpen, trackingSource]);

  const handleClosePopup = useCallback(() => {
    setViewSkillsProfessional(null);
    onClosePopup();
  }, [onClosePopup]);

  const handleConfirmProfessionals = useCallback(() => {
    onTrackEvent?.();

    if (onConfirm) {
      onConfirm([...selectedProfessionals]);

      return;
    }

    // Fire invite_sent event when popup actually sends invites
    if (trackingSource) {
      const selectedIds = [...selectedProfessionals];
      const favoriteCount = selectedIds.filter((id) =>
        favoriteProfessionals?.some((p: any) => p.id === id),
      ).length;
      const workedWithCount = selectedIds.filter((id) =>
        recentProfessionals?.some((p: any) => p.id === id || p.professional?.id === id),
      ).length;
      const newToYouCount = Math.max(0, selectedIds.length - favoriteCount - workedWithCount);

      fireInviteSent({
        shiftId: jobId || jobIds?.[0],
        totalInvitesSent: selectedIds.length,
        favoriteInvitesSent: favoriteCount,
        workedWithInvitesSent: workedWithCount,
        newToYouInvitesSent: newToYouCount,
        source: trackingSource,
      });
    }

    onSentInvitations();
  }, [
    onConfirm,
    onTrackEvent,
    selectedProfessionals,
    onSentInvitations,
    trackingSource,
    favoriteProfessionals,
    recentProfessionals,
    jobId,
    jobIds,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {!isSuccess ? (
        <Overlay>
          <ClickAwayListener onClickAway={() => handleClosePopup()}>
            <Container>
              <AnimatePresence mode="wait">
                {isNewDesign && viewSkillsProfessional ? (
                  <motion.div
                    key="view-skills"
                    initial={{ x: '100%', opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: '100%', opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'column',
                    }}
                  >
                    <ViewSkillsScreen
                      fullName={viewSkillsProfessional.fullName}
                      profession={viewSkillsProfessional.profession}
                      professionCode={viewSkillsProfessional.professionCode}
                      education={viewSkillsProfessional.education}
                      onBack={() => setViewSkillsProfessional(null)}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="invite-list"
                    data-cy="invite-popup"
                    initial={false}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: '-30%', opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'column',
                    }}
                  >
                    <Pressable onPress={handleClosePopup} dataCy="invite-popup-close">
                      <OutlineXIcon size={24} />
                    </Pressable>

                    <Column marginBottom={24} marginTop={54}>
                      <Typography as="span" bold size="body_large" textAlign="center">
                        Invite Professionals
                      </Typography>
                    </Column>

                    {isLoading && (
                      <Center flex={1}>
                        <Spinner centered variant="default" size="md" />
                      </Center>
                    )}

                    {!isLoading && (
                      <>
                        <Tabs
                          defaultValue="favorites"
                          value={activeTab}
                          onValueChange={(value) =>
                            onTabChange(value as Parameters<typeof onTabChange>[0])
                          }
                          style={{ flex: 1, gap: 16 }}
                        >
                          <Tabs.List
                            fullWidth
                            style={{
                              gap: 20,
                              marginBottom: isNewDesign ? 0 : 8,
                              paddingInline: 20,
                            }}
                          >
                            <Tabs.Tab
                              value="favorites"
                              label={
                                favoriteProfessionals?.length > 0
                                  ? String(favoriteProfessionals.length)
                                  : undefined
                              }
                              data-cy="invite-popup-tab-favorites"
                            >
                              Favorites
                            </Tabs.Tab>
                            <Tabs.Tab
                              value="worked-with"
                              label={
                                recentProfessionals?.length > 0
                                  ? String(recentProfessionals.length)
                                  : undefined
                              }
                              data-cy="invite-popup-tab-worked-with"
                            >
                              Worked With
                            </Tabs.Tab>
                            <Tabs.Tab
                              value="new-to-you"
                              label={
                                recommendedProfessionals?.length > 0
                                  ? String(recommendedProfessionals.length)
                                  : undefined
                              }
                              data-cy="invite-popup-tab-new-to-you"
                            >
                              New to You
                            </Tabs.Tab>
                          </Tabs.List>
                          <Tabs.Panel
                            value="favorites"
                            style={{ display: 'flex', flex: 1, width: '100%' }}
                          >
                            <FavoriteTabPanel
                              isLoading={isLoading}
                              selectedProfessionals={selectedProfessionals}
                              onSelectProfessionals={onSelectProfessionals}
                              profession={profession ?? jobProfession}
                              data={favoriteProfessionals ?? []}
                              isNewDesign={isNewDesign}
                              onViewSkills={(pro) => setViewSkillsProfessional(pro)}
                              allSelectableIds={allSelectableIds}
                              jobId={jobId}
                              sentProfessionalIds={sentProfessionalIds}
                            />
                          </Tabs.Panel>
                          <Tabs.Panel
                            value="worked-with"
                            style={{ display: 'flex', flex: 1, width: '100%' }}
                          >
                            <RecentProfessionalTabPanel
                              isLoading={isLoading}
                              selectedProfessionals={selectedProfessionals}
                              onSelectProfessionals={onSelectProfessionals}
                              profession={profession ?? jobProfession}
                              data={recentProfessionals ?? []}
                              isNewDesign={isNewDesign}
                              onViewSkills={(pro) => setViewSkillsProfessional(pro)}
                              allSelectableIds={allSelectableIds}
                              jobId={jobId}
                              sentProfessionalIds={sentProfessionalIds}
                            />
                          </Tabs.Panel>
                          <Tabs.Panel
                            value="new-to-you"
                            style={{ display: 'flex', flex: 1, width: '100%' }}
                          >
                            <RecommendedProsTabPanel
                              isLoading={isLoading}
                              selectedProfessionals={selectedProfessionals}
                              onSelectProfessionals={onSelectProfessionals}
                              profession={profession ?? jobProfession}
                              data={recommendedProfessionals}
                              isNewDesign={isNewDesign}
                              onViewSkills={(pro) => setViewSkillsProfessional(pro)}
                              allSelectableIds={allSelectableIds}
                              jobId={jobId}
                              sentProfessionalIds={sentProfessionalIds}
                            />
                          </Tabs.Panel>
                        </Tabs>
                        <Button
                          data-cy="invite-popup-confirm"
                          disabled={isNewDesign && selectedProfessionals.size === 0}
                          isLoading={isInviting && !isLoading}
                          size="md"
                          style={{ marginInline: 'auto', marginBottom: 32, marginTop: 24 }}
                          onClick={handleConfirmProfessionals}
                        >
                          {buttonText}
                        </Button>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </Container>
          </ClickAwayListener>
        </Overlay>
      ) : (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <AnimatedCheckCircle />
            </div>
          )}
          dataCy="invite-popup-success"
          rightButtonDataCy="invite-popup-success-close"
          title="Invite(s) Successfully Sent!"
          description="This shift request has been successfully sent to your selected professionals."
          rightButtonAction={handleClosePopup}
          rightButtonText="Close"
        />
      )}
    </>
  );
};
